import React, { memo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Volume2, VolumeX, Eye, EyeOff, RotateCcw, Info, AlertTriangle, GraduationCap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useApp } from '../context/AppContext';
import { STORAGE_KEYS } from '../hooks/useLocalStorage';

const Settings = memo(function Settings() {
  const navigate = useNavigate();
  const { settings, updateSettings } = useApp();
  const [showResetDialog, setShowResetDialog] = useState(false);
  
  const handleToggleSound = () => {
    updateSettings({ soundEnabled: !settings.soundEnabled });
  };
  
  const handleToggleNoteNames = () => {
    updateSettings({ showNoteNames: !settings.showNoteNames });
  };
  
  const handleToggleLearningMode = () => {
    updateSettings({ learningMode: !settings.learningMode });
  };
  
  const handleResetProgress = () => {
    // Clear all localStorage data for Glissando
    Object.values(STORAGE_KEYS).forEach(key => {
      window.localStorage.removeItem(key);
    });
    
    // Close dialog
    setShowResetDialog(false);
    
    // Redirect to home and force reload to reset all state
    window.location.href = '/';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="min-h-screen bg-cream-100 paper-texture"
      data-testid="settings-screen"
    >
      <div className="max-w-lg mx-auto px-6 py-8">
        {/* Header */}
        <header className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate(-1)}
            className="icon-btn w-10 h-10 rounded-full flex items-center justify-center transition-colors"
            style={{ backgroundColor: 'rgba(107, 127, 140, 0.1)' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(107, 127, 140, 0.2)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(107, 127, 140, 0.1)'}
            onMouseDown={(e) => e.currentTarget.style.backgroundColor = 'rgba(107, 127, 140, 0.3)'}
            onMouseUp={(e) => e.currentTarget.style.backgroundColor = 'rgba(107, 127, 140, 0.2)'}
            aria-label="Go back"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" style={{ color: '#6B7F8C' }} strokeWidth={1.5} />
          </button>
          <h1 className="font-serif text-2xl text-cream-900">Settings</h1>
        </header>
        
        {/* Practice Settings */}
        <section className="mb-8">
          <h2 className="text-xs uppercase tracking-widest text-wood-700 font-sans font-semibold mb-4">
            Practice
          </h2>
          
          <Card className="border-wood-300 bg-cream-50 rounded-card">
            <CardContent className="divide-y divide-wood-200">
              {/* Sound Toggle */}
              <div className="flex items-center justify-between py-4 first:pt-4 last:pb-4">
                <div className="flex items-center gap-3">
                  {settings.soundEnabled ? (
                    <Volume2 className="w-5 h-5" style={{ color: '#6B7F8C' }} strokeWidth={1.5} />
                  ) : (
                    <VolumeX className="w-5 h-5" style={{ color: '#6B7F8C' }} strokeWidth={1.5} />
                  )}
                  <div>
                    <p className="font-sans text-cream-900 font-medium">Sound Effects</p>
                    <p className="font-sans text-sm text-cream-800">Piano sounds and feedback</p>
                  </div>
                </div>
                <Switch
                  checked={settings.soundEnabled}
                  onCheckedChange={handleToggleSound}
                  data-testid="sound-toggle"
                />
              </div>
              
              {/* Note Names Toggle */}
              <div className="flex items-center justify-between py-4">
                <div className="flex items-center gap-3">
                  {settings.showNoteNames ? (
                    <Eye className="w-5 h-5" style={{ color: '#6B7F8C' }} strokeWidth={1.5} />
                  ) : (
                    <EyeOff className="w-5 h-5" style={{ color: '#6B7F8C' }} strokeWidth={1.5} />
                  )}
                  <div>
                    <p className="font-sans text-cream-900 font-medium">Show Note Names</p>
                    <p className="font-sans text-sm text-cream-800">Display labels on piano keys</p>
                  </div>
                </div>
                <Switch
                  checked={settings.showNoteNames}
                  onCheckedChange={handleToggleNoteNames}
                  data-testid="notenames-toggle"
                />
              </div>
              
              {/* Learning Mode Toggle */}
              <div className="flex items-center justify-between py-4">
                <div className="flex items-center gap-3">
                  <GraduationCap className={`w-5 h-5`} style={{ color: '#6B7F8C' }} strokeWidth={1.5} />
                  <div>
                    <p className="font-sans text-cream-900 font-medium">Learning Mode</p>
                    <p className="font-sans text-sm text-cream-800">Show note guides on staff lines</p>
                  </div>
                </div>
                <Switch
                  checked={settings.learningMode}
                  onCheckedChange={handleToggleLearningMode}
                  data-testid="learningmode-toggle"
                />
              </div>
            </CardContent>
          </Card>
        </section>
        
        {/* Data Management */}
        <section className="mb-8">
          <h2 className="text-xs uppercase tracking-widest text-wood-700 font-sans font-semibold mb-4">
            Data
          </h2>
          
          <Card className="border-wood-300 bg-cream-50 rounded-card">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <RotateCcw className="w-5 h-5" style={{ color: '#6B7F8C' }} strokeWidth={1.5} />
                  <div>
                    <p className="font-sans text-cream-900 font-medium">Reset Progress</p>
                    <p className="font-sans text-sm text-cream-800">Clear all data and start fresh</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowResetDialog(true)}
                  className="text-coral-500 border-coral-500/50 hover:bg-coral-500/10 active:bg-coral-500/20 transition-colors"
                  data-testid="reset-progress-btn"
                >
                  Reset
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>
        
        {/* About */}
        <section>
          <h2 className="text-xs uppercase tracking-widest text-wood-700 font-sans font-semibold mb-4">
            About
          </h2>
          
          <Card className="border-wood-300 bg-cream-50 rounded-card">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#6B7F8C' }} strokeWidth={1.5} />
                <div>
                  <p className="font-serif text-lg text-cream-900 mb-1">Glissando</p>
                  <p className="font-sans text-sm text-cream-800 mb-3">
                    A sight-reading practice app for adult piano learners. Master note recognition through intelligent spaced repetition.
                  </p>
                  <p className="font-sans text-xs text-cream-800">
                    Version 1.0.0 • All data stored locally
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
      
      {/* Reset Confirmation Dialog */}
      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent className="bg-cream-100 border-wood-300 max-w-sm rounded-popup">
          <AlertDialogHeader className="text-center sm:text-center">
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 rounded-full bg-coral-100 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-coral-500" strokeWidth={1.5} />
              </div>
            </div>
            <AlertDialogTitle className="font-serif text-xl text-cream-900">
              Reset All Progress?
            </AlertDialogTitle>
            <AlertDialogDescription className="font-sans text-cream-800 text-sm mt-2">
              This will permanently delete all your practice data, including your streak, mastery progress, and session history. You'll start fresh with the introduction tutorial.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-3 sm:flex-col mt-4">
            <AlertDialogAction
              onClick={handleResetProgress}
              className="w-full bg-coral-500 hover:bg-coral-600 active:bg-coral-700 text-white font-sans font-medium py-5 rounded-btn transition-colors"
              data-testid="confirm-reset-btn"
            >
              Yes, Reset Everything
            </AlertDialogAction>
            <AlertDialogCancel 
              className="w-full bg-transparent border-wood-300 hover:bg-wood-100 active:bg-wood-200 text-cream-900 font-sans font-medium py-5 rounded-btn mt-0 transition-colors"
              data-testid="cancel-reset-btn"
            >
              Cancel
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
});

export default Settings;
